import React from 'react';
import { Layout } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import NavBar from './NavBar';
import SideDrawer from './SideDrawer';
import Footer from './Footer';
import type { MenuItem } from './MainMenu';
import Header from './Header';
import type { HeaderProps } from './Header';
import PageTitle from '../common/PageTitle';

export interface MainLayoutProps {
  menuItems: MenuItem[];
  showDrawer: boolean;
  showNav: boolean;
  showHeader: boolean;
  logo?: React.ReactNode;
  footerContent?: React.ReactNode;
  navBarProps?: {
    extraContent?: React.ReactNode;
  };
  headerProps?: Omit<HeaderProps, 'headerText' | 'headerIcon'>;
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  menuItems,
  showDrawer,
  showNav,
  showHeader,
  logo,
  footerContent,
  navBarProps,
  headerProps,
  children,
}) => {
  const location = useLocation();

  // Find active menu item based on current route
  // Enhanced to support path prefixes for nested routes
  const findActiveMenuItem = (path: string, items: MenuItem[]): MenuItem | null => {
    // First try to find exact path match
    for (const item of items) {
      if (item.path === path) {
        return item;
      }
      if (item.children) {
        const activeChild = findActiveMenuItem(path, item.children);
        if (activeChild) return activeChild;
      }
    }

    // If no exact match, check for path prefixes
    for (const item of items) {
      // Skip root path when checking prefixes to avoid matching everything
      if (item.path !== '/' && path.startsWith(item.path)) {
        return item;
      }
    }

    return null;
  };

  const activeMenuItem = findActiveMenuItem(location.pathname, menuItems);

  return (
    <>
      <PageTitle />
      <Layout
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showNav && (
          <NavBar logo={logo} menuItems={menuItems} extraContent={navBarProps?.extraContent} />
        )}

        <Layout hasSider={showDrawer} style={{ flex: 1 }}>
          {showDrawer && <SideDrawer menuItems={menuItems} logo={logo} />}

          <Layout.Content
            style={{
              minHeight: 280,
              width: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {showHeader && (
              <Header
                headerText={activeMenuItem?.label}
                headerIcon={activeMenuItem?.icon}
                {...headerProps}
              />
            )}
            <div style={{ flex: 1 }} className="mx-5 mt-4">
              {children || <Outlet />}
            </div>
            <Layout.Footer>
              <Footer content={footerContent} />
            </Layout.Footer>
          </Layout.Content>
        </Layout>
      </Layout>
    </>
  );
};

export default MainLayout;
